import {
  fetchCurrentGallery,
  sealCurrentGallery,
  uploadTourPhoto
} from '@api/taptotip/tourPhotos';
import TopNav from '@components/Nav/TopNav';
import TipDirectLeftMenu from '@components/Nav/TopNav/TipDirectLeftMenu';
import { StyleBreakpoints } from '@constants';
import { makeStyles } from '@material-ui/core/styles';
import { formatRegionalLinks, isTourPhotosEnabledForCompany } from '@utils/helpers';
import * as queue from '@utils/tourPhotoQueue';
import { QRCodeCanvas } from 'qrcode.react';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import messages from './messages';

/**
 * Card 305 — tour photo capture for guides who work from the browser.
 *
 * The mobile app got this first (card 277), but a fair number of guides only
 * ever use the web app, and for them the feature did not exist at all.
 *
 * Same contract as the app deliberately: capture and QR live on one screen so
 * showing the wrong group their photos is impossible rather than a matter of
 * discipline, the first photo opens a session implicitly, and the group is told
 * before the camera is offered.
 */

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      flexDirection: 'row'
    }
  }
}));

const CARD =
  'w-full bg-white rounded-3xl shadow-sm border border-solid border-slate-200 p-6 min-w-0';

export const TourPhotos = ({ user }) => {
  const classes = useStyles();
  const intl = useIntl();

  const [status, setStatus] = useState('loading');
  const [gallery, setGallery] = useState(null);
  const [items, setItems] = useState([]);
  const [notice, setNotice] = useState(null);
  const [confirmingNewTour, setConfirmingNewTour] = useState(false);
  const [hasAnnounced, setHasAnnounced] = useState(false);

  // Two inputs: one that opens the camera, one that opens the library. A single
  // input cannot do both — `capture` forces the camera and gives no way back.
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);

  // Mirrors `items` for the unmount cleanup, which must see the latest queue
  // without re-subscribing every time the queue changes.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const mountedRef = useRef(true);

  const loadGallery = () => {
    setStatus('loading');
    fetchCurrentGallery()
      .then(data => {
        if (!mountedRef.current) return;
        setGallery(data?.gallery || null);
        setStatus('ready');
      })
      .catch(() => {
        if (mountedRef.current) setStatus('error');
      });
  };

  useEffect(loadGallery, []);

  // Object URLs for the local previews are held by the queue; releasing them on
  // unmount keeps a long shift from leaking a blob per photo.
  //
  // Mount-only on purpose. This used to be keyed on [items], and React runs an
  // effect's cleanup before every re-run, so the preview was revoked the moment
  // the photo moved to "uploading" — a broken-image icon on every upload (found
  // in human QA, 2026-10-15).
  useEffect(
    () => () => {
      mountedRef.current = false;
      queue.previewUrls(itemsRef.current).forEach(URL.revokeObjectURL);
    },
    []
  );

  // One upload at a time, driven off the queue's own state so a progress tick
  // re-rendering cannot start the same photo twice. No cleanup on purpose: see
  // startNextUpload for why a per-run cancel dropped every upload's answer.
  //
  // The gallery refresh after each upload is a quiet one, so the grid and QR
  // stay on screen. If only the refresh fails, the photo is still on the
  // server; fall back to the full reload with its retry state.
  useEffect(() => {
    queue.startNextUpload({
      items,
      setItems,
      upload: (item, onProgress) => uploadTourPhoto({ file: item.file, onProgress }),
      afterUpload: () =>
        fetchCurrentGallery()
          .then(data => {
            if (mountedRef.current) setGallery(data?.gallery || null);
          })
          .catch(() => {
            if (mountedRef.current) setStatus('error');
          }),
      isMounted: () => mountedRef.current
    });
  }, [items]);

  const serverPhotos = gallery?.photos || [];
  const serverPhotosRef = useRef(serverPhotos);
  serverPhotosRef.current = serverPhotos;

  // A finished photo is shown from the server once the server has it, never
  // from both places: waiting for the whole queue to go idle drew 8 tiles for
  // 6 photos mid-upload and disabled the buttons as "full" (2026-10-15).
  // `queued` is what is drawn and counted; this effect keeps the state in step
  // with it and releases each preview once its server copy has replaced it.
  const queued = queue.reconcile(items, serverPhotos);
  useEffect(() => {
    const kept = queue.reconcile(items, gallery?.photos);
    if (kept === items) return;
    queue
      .previewUrls(items.filter(item => !kept.includes(item)))
      .forEach(URL.revokeObjectURL);
    setItems(current => queue.reconcile(current, gallery?.photos));
  }, [items, gallery]);

  const room = queue.remainingSlots(queued, serverPhotos.length);
  const hasAnything = serverPhotos.length > 0 || queued.length > 0;

  const handleFiles = event => {
    const files = Array.from(event.target.files || []);
    // Reset so picking the same file twice in a row still fires a change event.
    event.target.value = '';
    if (files.length === 0) return;

    // Built on the queue as it is when the chooser returns (the latest
    // committed items and server photos, read from refs), not on a list
    // captured before it opened: see queue.addFiles.
    const result = queue.addFiles(itemsRef.current, files, serverPhotosRef.current);
    itemsRef.current = result.items;
    setItems(result.items);
    if (result.rejected > 0) {
      setNotice(intl.formatMessage(messages.someRejected, { count: result.rejected }));
    }
  };

  const confirmNewTour = () => {
    setConfirmingNewTour(false);
    queue.previewUrls(items).forEach(URL.revokeObjectURL);
    setItems([]);
    sealCurrentGallery()
      .then(loadGallery)
      .catch(() => setNotice(intl.formatMessage(messages.loadFailed)));
  };

  const guestUrl = gallery
    ? formatRegionalLinks(`t/g/${user.userId}/p?g=${gallery.token}`)
    : null;

  const renderBody = () => {
    if (status === 'loading') {
      return (
        <div
          className={CARD}
          role="status"
          aria-label={intl.formatMessage(messages.loading)}>
          <div className="grid grid-cols-3 gap-3">
            {[0, 1, 2, 3, 4, 5].map(i => (
              <div key={i} className="aspect-square bg-slate-100 rounded-2xl" />
            ))}
          </div>
        </div>
      );
    }

    if (status === 'error') {
      return (
        <div className={CARD}>
          <p className="text-slate-600 m-0 mb-4">
            {intl.formatMessage(messages.loadFailed)}
          </p>
          <button
            type="button"
            onClick={loadGallery}
            className="px-4 py-2 rounded-xl bg-slate-900 text-white border-0 cursor-pointer">
            {intl.formatMessage(messages.retry)}
          </button>
        </div>
      );
    }

    // Preventing capture beats deleting afterwards, and telling the group up
    // front is the single biggest expectation-setter in the legitimate-interest
    // balance. So it comes before the camera, not after.
    if (!hasAnnounced && !hasAnything) {
      return (
        <div className={`${CARD} !bg-indigo-50 !border-indigo-100`} data-testid="announce">
          <h2 className="text-lg font-semibold text-slate-900 m-0 mb-2">
            {intl.formatMessage(messages.announceTitle)}
          </h2>
          <p className="text-slate-600 m-0 mb-3">
            {intl.formatMessage(messages.announceBody)}
          </p>
          <p className="italic text-slate-800 m-0 mb-5">
            {intl.formatMessage(messages.announceScript)}
          </p>
          <button
            type="button"
            onClick={() => setHasAnnounced(true)}
            data-testid="announce-ack"
            className="px-4 py-2 rounded-xl bg-indigo-600 text-white border-0 cursor-pointer">
            {intl.formatMessage(messages.announceAck)}
          </button>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6 w-full">
        {!hasAnything && (
          <div className={CARD}>
            <h2 className="text-lg font-semibold text-slate-900 m-0 mb-2">
              {intl.formatMessage(messages.blankSlateTitle)}
            </h2>
            <p className="text-slate-600 m-0">
              {intl.formatMessage(messages.blankSlateBody)}
            </p>
          </div>
        )}

        {guestUrl && (
          <div className={CARD} data-testid="guest-qr">
            <h2 className="text-lg font-semibold text-slate-900 m-0 mb-2">
              {intl.formatMessage(messages.showGuests)}
            </h2>
            <p className="text-slate-600 m-0 mb-4">
              {intl.formatMessage(messages.showGuestsBody)}
            </p>
            <div className="flex justify-center">
              <QRCodeCanvas value={guestUrl} size={220} />
            </div>
          </div>
        )}

        {hasAnything && (
          <div className={CARD}>
            <div className="grid grid-cols-3 gap-3" data-testid="photo-grid">
              {serverPhotos.map(photo => (
                <img
                  key={photo.publicId}
                  src={photo.url}
                  alt=""
                  className="aspect-square w-full object-cover rounded-2xl"
                />
              ))}

              {queued.map(item => (
                <div key={item.id} className="relative">
                  <img
                    src={item.previewUrl}
                    alt=""
                    className="aspect-square w-full object-cover rounded-2xl opacity-60"
                  />

                  {item.status === queue.STATUS.uploading && (
                    <div className="absolute inset-x-2 bottom-2 h-1 bg-white/60 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-600"
                        style={{ width: `${Math.round(item.progress * 100)}%` }}
                        data-testid="upload-progress"
                      />
                    </div>
                  )}

                  {item.status === queue.STATUS.failed && (
                    <div className="absolute inset-0 rounded-2xl bg-slate-900/70 flex flex-col items-center justify-center gap-1 p-2">
                      <span className="text-white text-xs text-center">
                        {intl.formatMessage(messages.uploadFailed)}
                      </span>
                      <button
                        type="button"
                        data-testid="retry-photo"
                        onClick={() => setItems(current => queue.retry(current, item.id))}
                        className="text-white text-xs underline bg-transparent border-0 cursor-pointer">
                        {intl.formatMessage(messages.retry)}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
                          setItems(current => queue.remove(current, item.id));
                        }}
                        className="text-white/80 text-xs underline bg-transparent border-0 cursor-pointer">
                        {intl.formatMessage(messages.discard)}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <p className="text-sm text-slate-500 mt-4 mb-0" data-testid="photo-counter">
              {room > 0
                ? intl.formatMessage(messages.remaining, { count: room })
                : intl.formatMessage(messages.full)}
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          {/* The first `capture` input in this app. Without it a phone opens the
              file picker, which is the wrong interaction for a guide standing
              in front of a group. `environment` asks for the rear camera. */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFiles}
            className="hidden"
            data-testid="camera-input"
          />
          <input
            ref={libraryInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFiles}
            className="hidden"
            data-testid="library-input"
          />

          <button
            type="button"
            disabled={room === 0}
            onClick={() => cameraInputRef.current?.click()}
            data-testid="take-photo"
            className="px-5 py-3 rounded-xl bg-indigo-600 text-white border-0 cursor-pointer disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed">
            {intl.formatMessage(messages.takePhoto)}
          </button>
          <button
            type="button"
            disabled={room === 0}
            onClick={() => libraryInputRef.current?.click()}
            data-testid="add-photos"
            className="px-5 py-3 rounded-xl bg-white text-indigo-700 border border-solid border-indigo-200 cursor-pointer disabled:text-slate-400 disabled:border-slate-200 disabled:cursor-not-allowed">
            {intl.formatMessage(messages.addPhotos)}
          </button>

          {hasAnything && (
            <button
              type="button"
              onClick={() => setConfirmingNewTour(true)}
              data-testid="new-tour"
              className="px-5 py-3 rounded-xl bg-transparent text-slate-600 border-0 cursor-pointer underline">
              {intl.formatMessage(messages.newTour)}
            </button>
          )}
        </div>

        {/* Sealing is irreversible from the guide's side — they lose these
            photos the moment it happens — so it always asks first. */}
        {confirmingNewTour && (
          <div className={CARD} data-testid="new-tour-confirm">
            <h2 className="text-lg font-semibold text-slate-900 m-0 mb-2">
              {intl.formatMessage(messages.newTourConfirmTitle)}
            </h2>
            <p className="text-slate-600 m-0 mb-4">
              {intl.formatMessage(messages.newTourConfirmBody)}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={confirmNewTour}
                data-testid="new-tour-confirm-action"
                className="px-4 py-2 rounded-xl bg-slate-900 text-white border-0 cursor-pointer">
                {intl.formatMessage(messages.newTourConfirmAction)}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingNewTour(false)}
                className="px-4 py-2 rounded-xl bg-transparent text-slate-600 border-0 cursor-pointer">
                {intl.formatMessage(messages.cancel)}
              </button>
            </div>
          </div>
        )}

        {notice && (
          <div className={CARD} data-testid="notice">
            <p className="text-slate-700 m-0 mb-3">{notice}</p>
            <button
              type="button"
              onClick={() => setNotice(null)}
              className="px-4 py-2 rounded-xl bg-slate-900 text-white border-0 cursor-pointer">
              {intl.formatMessage(messages.cancel)}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Card 305 — the nav hides this tab until the company is switched on, but the
  // route is reachable by typing it. Guard here too: caught in browser testing,
  // where the tab was correctly absent and the page rendered anyway. Same
  // failure shape as card 304, so it gets the same answer.
  //
  // After every hook, so hook order cannot change between renders.
  if (!isTourPhotosEnabledForCompany(user)) {
    return <Redirect to="/" />;
  }

  return (
    <>
      <TopNav />
      <div className={classes.container}>
        <TipDirectLeftMenu user={user} selectedTab="TourPhotos" />
        <div className="flex flex-col gap-6 w-full p-4 sm:p-8 min-w-0">
          <h1 className="text-2xl font-bold text-slate-900 m-0">
            {intl.formatMessage(messages.title)}
          </h1>
          {renderBody()}
        </div>
      </div>
    </>
  );
};

TourPhotos.propTypes = {
  user: PropTypes.object
};

// Same shape the Dashboard uses; there is no makeSelectUser in this app.
const mapStateToProps = state => ({
  user: state.user?.user
});

export default connect(mapStateToProps)(TourPhotos);
